import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  FlatList,
  Modal,
  Alert,
  RefreshControl,
  Animated,
  Dimensions,
  Image,
  ActivityIndicator,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import UnifiedJobApplicationService from '../../services/UnifiedJobApplicationService';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Context imports with error handling
let useProfessional, useProfessionalOnboarding, useGeneral;
try {
  const professionalContext = require('../../context/ProfessionalContext');
  useProfessional = professionalContext.useProfessional;
} catch (error) {
  console.warn('ProfessionalContext not found, using mock');
  useProfessional = () => ({});
}

try {
  const onboardingContext = require('../../context/ProfessionalOnboardingContext');
  useProfessionalOnboarding = onboardingContext.useProfessionalOnboarding;
} catch (error) {
  console.warn('ProfessionalOnboardingContext not found, using mock');
  useProfessionalOnboarding = () => ({});
}

try {
  const generalContext = require('../../context/GeneralContext');
  useGeneral = generalContext.useGeneral;
} catch (error) {
  console.warn('GeneralContext not found, using mock');
  useGeneral = () => ({
    showToast: (message, type) => console.log(`Toast: ${message} (${type})`),
  });
}

const { width, height } = Dimensions.get('window');

// ─────────────────────────────────────────────────────────────────────────────
// API → UI normalization
// The /api/data/applications endpoint returns snake_case rows. The renderer
// expects camelCase. This function is the single source of truth for that
// mapping — any new field accessed by the UI should be normalized here.
// ─────────────────────────────────────────────────────────────────────────────
const PROGRESS_BY_STATUS = {
  applied: 10,
  pending: 10,
  submitted: 15,
  under_review: 25,
  reviewed: 30,
  shortlisted: 40,
  assessment: 45,
  tests_competencies: 45,
  interview_scheduled: 55,
  interview_completed: 65,
  reference_check: 75,
  offer: 85,
  offer_letter: 85,
  hired: 100,
  rejected: 100,
  withdrawn: 100,
};

const TERMINAL_STATUSES = new Set(['withdrawn', 'rejected', 'hired']);

const normalizeApplication = (a) => {
  if (!a || typeof a !== 'object') return null;
  const status = String(a.status || a.stage || 'pending').toLowerCase();
  const cfgProgress =
    UnifiedJobApplicationService?.STATUS_CONFIG?.[status]?.progress;
  const progress =
    typeof a.progress === 'number'
      ? a.progress
      : typeof cfgProgress === 'number'
      ? cfgProgress
      : PROGRESS_BY_STATUS[status] ?? 10;

  return {
    id: String(a.id ?? a.application_id ?? `app_${Date.now()}_${Math.random()}`),
    jobId: a.job_id ?? a.jobId ?? null,
    jobTitle:
      a.job_title ||
      a.position ||
      a.current_title ||
      a.title ||
      'Job Application',
    company:
      a.employer_name ||      // ← new endpoint (canonical)
      a.company_name ||
      a.company ||
      a.employer ||
      '',
    companyLogo:
      a.employer_logo ||      // ← new endpoint
      a.company_logo ||
      a.logo ||
      null,
    location:
      a.job_location ||       // ← new endpoint (the job's location, not candidate's)
      a.location ||
      a.candidate_location ||
      '',
    salary:
      // Build a salary range string from min/max if either is present
      (a.job_salary_min || a.job_salary_max)
        ? [
            a.job_salary_min ? `£${Number(a.job_salary_min).toLocaleString()}` : '',
            a.job_salary_max ? `£${Number(a.job_salary_max).toLocaleString()}` : '',
          ].filter(Boolean).join(' – ')
        : (a.salary || a.expected_salary || a.salary_range || ''),
    jobType: a.job_type || a.employment_type || '',
    isRemote: !!a.job_is_remote,
    status,
    applicationDate:
      a.applied_date ||
      a.application_date ||
      a.created_at ||
      new Date().toISOString(),
    lastUpdated:
      a.updated_at ||
      a.last_updated ||
      a.applied_date ||
      a.created_at ||
      new Date().toISOString(),
    matchScore: Number(a.match_score ?? a.ai_score ?? a.overall_score ?? 0) || 0,
    progress,
    statusHistory: Array.isArray(a.status_history) ? a.status_history : [],
    canWithdraw: !TERMINAL_STATUSES.has(status),
    // Candidate identity from JOIN — useful for the detail view
    candidateName: a.candidate_name || '',
    candidateAvatar: a.candidate_avatar || null,
    candidateEmail: a.candidate_email || '',
    // keep the raw row for debugging / future fields without forcing a rewrite
    _raw: a,
  };
};

const ApplicationsTrackingScreen = ({ navigation, route }) => {
  // ── Profile integration ──
  const profileCtx = useProfile();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [apiError, setApiError] = useState(null);

  // Context hooks with error handling
  const professional = useProfessional ? useProfessional() : {};
  const onboarding = useProfessionalOnboarding ? useProfessionalOnboarding() : {};
  const { showToast } = useGeneral ? useGeneral() : { showToast: () => {} };

  // Ref to avoid stale closures in useFocusEffect
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // DATA LOADING — single source of truth
  //
  // The previous version had two parallel data paths that never met:
  //   (a) an orphan useEffect that fetched the API and wrote it to an
  //       AsyncStorage key nobody read.
  //   (b) loadApplications() which only consulted UnifiedJobApplicationService
  //       (local cache), so the screen never reflected the database.
  //
  // Now the API is primary, normalized into the UI shape, and pushed to state.
  // The unified service is used as an offline fallback only.
  // ───────────────────────────────────────────────────────────────────────────
  const loadApplications = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setApiError(null);

    try {
      // Initialize local service so notifications/analytics still work.
      try { await UnifiedJobApplicationService.initialize(); } catch (e) {
        console.warn('UnifiedJobApplicationService.initialize failed:', e?.message);
      }

      // PRIMARY: live database via apiFetch — candidate-scoped endpoint that
      // JOINs jobs + company_profiles + users so we get job_title, employer_name,
      // employer_logo, candidate_name (real name), etc. all in one round trip.
      const email =
        (await AsyncStorage.getItem('user_email')) ||
        (await AsyncStorage.getItem('userEmail')) ||
        '';
      const endpoint = email
        ? `/api/professional/my-applications?email=${encodeURIComponent(email)}&limit=100`
        : '/api/data/applications?sort=applied_date&order=desc&limit=100';
      const resp = await apiFetch(endpoint);

      const rawRows = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.results)
        ? resp.results
        : null;

      if (!rawRows) {
        // API responded but shape is unexpected — fall through to cache
        throw new Error(
          resp?.error || resp?.message || 'Unexpected API response'
        );
      }

      const normalized = rawRows
        .map(normalizeApplication)
        .filter(Boolean);

      if (!isMounted.current) return;
      setApplications(normalized);
      console.log(`✅ Applications loaded from API: ${normalized.length}`);

      // Best-effort cache push so other screens / offline mode stay in sync.
      try {
        if (typeof UnifiedJobApplicationService.setApplications === 'function') {
          await UnifiedJobApplicationService.setApplications(normalized);
        } else if (typeof UnifiedJobApplicationService.cacheApplications === 'function') {
          await UnifiedJobApplicationService.cacheApplications(normalized);
        }
      } catch (e) {
        // Non-fatal — caching is opportunistic.
      }
    } catch (error) {
      console.error('❌ API load failed, trying local cache:', error?.message);
      setApiError(error?.message || 'Could not reach server');

      // FALLBACK: local cache via UnifiedJobApplicationService
      try {
        const cached = await UnifiedJobApplicationService.getAllApplications();
        if (Array.isArray(cached) && cached.length > 0) {
          const normalized = cached.map(normalizeApplication).filter(Boolean);
          if (isMounted.current) setApplications(normalized);
          showToast?.('Showing cached applications (offline)', 'info');
        } else if (isMounted.current) {
          setApplications([]);
        }
      } catch (cacheErr) {
        console.error('❌ Cache fallback also failed:', cacheErr);
        if (isMounted.current) setApplications([]);
        showToast?.('Failed to load applications', 'error');
      }
    } finally {
      if (isMounted.current && !silent) setLoading(false);
    }
  }, [showToast]);

  const loadNotifications = useCallback(async () => {
    try {
      const notifs = await UnifiedJobApplicationService.getNotifications();
      if (isMounted.current) setNotifications(Array.isArray(notifs) ? notifs : []);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      let analyticsData = await UnifiedJobApplicationService.getAnalytics();
      if (!analyticsData) {
        try { await UnifiedJobApplicationService.updateAnalytics(); } catch {}
        analyticsData = await UnifiedJobApplicationService.getAnalytics();
      }
      if (isMounted.current && analyticsData) setAnalytics(analyticsData);
    } catch (error) {
      console.error('❌ Error loading analytics:', error);
    }
  }, []);

  // Load data on screen focus
  useFocusEffect(
    useCallback(() => {
      loadApplications();
      loadNotifications();
      loadAnalytics();

      // Check if coming from application submission
      if (route?.params?.fromSubmission && route?.params?.applicationId) {
        setTimeout(() => {
          showToast?.('Application submitted successfully!', 'success');
        }, 1000);
      }
    }, [loadApplications, loadNotifications, loadAnalytics, route?.params, showToast])
  );

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadApplications({ silent: true }),
        loadNotifications(),
        loadAnalytics(),
      ]);
    } finally {
      if (isMounted.current) setRefreshing(false);
    }
  };

  const handleApplicationPress = (application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  const handleStageAction = async (application) => {
    const statusConfig = UnifiedJobApplicationService.STATUS_CONFIG[application.status] || {};

    if (statusConfig.actionScreen) {
      switch (statusConfig.actionScreen) {
        case 'JobApplication':
          navigation.navigate('JobApplication', {
            applicationId: application.id,
            jobId: application.jobId,
            job: {
              id: application.jobId,
              title: application.jobTitle,
              company: application.company,
              location: application.location,
              salary: application.salary,
              jobType: application.jobType
            }
          });
          break;
        case 'ActionScreen':
          navigation.navigate('ActionScreen', {
            actionType: getActionTypeFromStatus(application.status),
            applicationData: application,
            jobData: {
              id: application.jobId,
              title: application.jobTitle,
              company: application.company
            }
          });
          break;
        default:
          navigation.navigate('ActionScreen', {
            actionType: 'general',
            applicationData: application,
            jobData: {
              id: application.jobId,
              title: application.jobTitle,
              company: application.company
            }
          });
          break;
      }
    } else {
      showToast?.(statusConfig.nextAction || 'No action required at this time', 'info');
    }
  };

  const getActionTypeFromStatus = (status) => {
    switch (status) {
      case UnifiedJobApplicationService.APPLICATION_STATUS?.TESTS_COMPETENCIES:
        return 'assessment';
      case UnifiedJobApplicationService.APPLICATION_STATUS?.INTERVIEW_SCHEDULED:
        return 'interview';
      case UnifiedJobApplicationService.APPLICATION_STATUS?.OFFER_LETTER:
        return 'offer';
      case UnifiedJobApplicationService.APPLICATION_STATUS?.HIRED:
        return 'onboarding';
      default:
        return 'general';
    }
  };

  const handleWithdrawApplication = async (applicationId) => {
    Alert.alert(
      'Withdraw Application',
      'Are you sure you want to withdraw this application? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Withdraw',
          style: 'destructive',
          onPress: async () => {
            try {
              // Update database first
              const r = await apiFetch(`/api/data/applications/${applicationId}`, {
                method: 'PUT',
                body: JSON.stringify({ status: 'withdrawn' }),
              });
              if (r?.error) throw new Error(r.error);

              // Mirror to local service (best-effort)
              try {
                await UnifiedJobApplicationService.updateApplicationStatus(
                  applicationId,
                  UnifiedJobApplicationService.APPLICATION_STATUS?.WITHDRAWN || 'withdrawn',
                  'Application withdrawn by user'
                );
              } catch {}

              await loadApplications({ silent: true });
              setShowDetailsModal(false);
              showToast?.('Application withdrawn', 'info');
            } catch (error) {
              console.error('Error withdrawing application:', error);
              showToast?.('Failed to withdraw application', 'error');
            }
          }
        }
      ]
    );
  };

  const handleDeleteApplication = async (applicationId) => {
    Alert.alert(
      'Delete Application',
      'Are you sure you want to delete this application? This will remove it from your tracking list.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const r = await apiFetch(`/api/data/applications/${applicationId}`, {
                method: 'DELETE',
              });
              if (r?.error) throw new Error(r.error);

              try {
                await UnifiedJobApplicationService.deleteApplication(applicationId);
              } catch {}

              await loadApplications({ silent: true });
              setShowDetailsModal(false);
              showToast?.('Application deleted', 'info');
            } catch (error) {
              console.error('Error deleting application:', error);
              showToast?.('Failed to delete application', 'error');
            }
          }
        }
      ]
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFilteredApplications = () => {
    let filtered = [...applications];

    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.applicationDate) - new Date(a.applicationDate);
        case 'company':
          return (a.company || '').localeCompare(b.company || '');
        case 'status':
          return (a.progress || 0) - (b.progress || 0);
        case 'title':
          return (a.jobTitle || '').localeCompare(b.jobTitle || '');
        default:
          return 0;
      }
    });

    return filtered;
  };

  const renderApplicationCard = ({ item }) => {
    const statusConfig = UnifiedJobApplicationService.STATUS_CONFIG[item.status] || {};
    const hasAction = statusConfig.actionScreen || statusConfig.nextAction;

    return (
      <TouchableOpacity
        style={styles.applicationCard}
        onPress={() => handleApplicationPress(item)}
        activeOpacity={0.7}
      >
        <LinearGradient
          colors={['#ffffff', '#f8f9fa']}
          style={styles.cardGradient}
        >
          {/* Header */}
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <Text style={styles.jobTitle} numberOfLines={1}>
                {item.jobTitle}
              </Text>
              <Text style={styles.company} numberOfLines={1}>
                {item.company}
              </Text>
            </View>
            <View style={styles.cardHeaderRight}>
              <View style={[styles.statusIndicator, { backgroundColor: statusConfig.color || '#667eea' }]}>
                <Ionicons name={statusConfig.icon || 'document-text'} size={16} color="white" />
              </View>
            </View>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${item.progress}%`,
                    backgroundColor: statusConfig.color || '#667eea',
                  },
                ]}
              />
            </View>
            <Text style={styles.progressText}>{item.progress}%</Text>
          </View>

          {/* Status Info */}
          <View style={styles.statusInfo}>
            <Text style={[styles.statusLabel, { color: statusConfig.color || '#667eea' }]}>
              {statusConfig.label || item.status}
            </Text>
            <Text style={styles.statusDescription} numberOfLines={2}>
              {statusConfig.description || ''}
            </Text>
          </View>

          {/* Additional Info */}
          {item.matchScore > 0 && (
            <View style={styles.matchContainer}>
              <View style={styles.matchBadge}>
                <Ionicons name="star" size={14} color="#f39c12" />
                <Text style={styles.matchText}>
                  {item.matchScore}% match
                </Text>
              </View>
            </View>
          )}

          {/* Footer */}
          <View style={styles.cardFooter}>
            <Text style={styles.applicationDate}>
              Applied: {formatDate(item.applicationDate)}
            </Text>
            {hasAction && (
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: statusConfig.color || '#667eea' }]}
                onPress={() => handleStageAction(item)}
              >
                <Text style={styles.actionButtonText}>
                  {statusConfig.nextAction || 'Take Action'}
                </Text>
                <Ionicons name="chevron-forward" size={16} color="white" />
              </TouchableOpacity>
            )}
          </View>
        </LinearGradient>
      </TouchableOpacity>
    );
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <LinearGradient
        colors={['#667eea', '#764ba2']}
        style={styles.headerGradient}
      >
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="white" />
            </TouchableOpacity>
            <View>
              <Text style={styles.headerTitle}>My Applications</Text>
              <Text style={styles.headerSubtitle}>
                Track your job applications
              </Text>
            </View>
          </View>
          <TouchableOpacity
            style={styles.notificationButton}
            onPress={() => setShowNotifications(true)}
          >
            <Ionicons name="notifications" size={24} color="white" />
            {notifications.filter(n => !n.read).length > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>
                  {notifications.filter(n => !n.read).length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );

  const renderAnalytics = () => {
    if (!analytics) return null;

    return (
      <View style={styles.analyticsContainer}>
        <Text style={styles.analyticsTitle}>Overview</Text>
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticCard}>
            <Text style={styles.analyticNumber}>{analytics.totalApplications}</Text>
            <Text style={styles.analyticLabel}>Total</Text>
          </View>
          <View style={styles.analyticCard}>
            <Text style={styles.analyticNumber}>{analytics.activeApplications}</Text>
            <Text style={styles.analyticLabel}>Active</Text>
          </View>
          <View style={styles.analyticCard}>
            <Text style={styles.analyticNumber}>{analytics.interviewApplications}</Text>
            <Text style={styles.analyticLabel}>Interviews</Text>
          </View>
          <View style={styles.analyticCard}>
            <Text style={styles.analyticNumber}>{analytics.hiredApplications}</Text>
            <Text style={styles.analyticLabel}>Hired</Text>
          </View>
        </View>

        {analytics.successRate > 0 && (
          <View style={styles.successRateContainer}>
            <Text style={styles.successRateText}>
              Success Rate: {analytics.successRate}%
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderFilters = () => (
    <View style={styles.filtersContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <TouchableOpacity
          style={[
            styles.filterButton,
            filterStatus === 'all' && styles.filterButtonActive,
          ]}
          onPress={() => setFilterStatus('all')}
        >
          <Text
            style={[
              styles.filterButtonText,
              filterStatus === 'all' && styles.filterButtonTextActive,
            ]}
          >
            All
          </Text>
        </TouchableOpacity>
        {Object.entries(UnifiedJobApplicationService.APPLICATION_STATUS || {}).map(([key, value]) => (
          <TouchableOpacity
            key={value}
            style={[
              styles.filterButton,
              filterStatus === value && styles.filterButtonActive,
            ]}
            onPress={() => setFilterStatus(value)}
          >
            <Text
              style={[
                styles.filterButtonText,
                filterStatus === value && styles.filterButtonTextActive,
              ]}
            >
              {UnifiedJobApplicationService.STATUS_CONFIG?.[value]?.label || key}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderApplicationDetailsModal = () => (
    <Modal
      visible={showDetailsModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowDetailsModal(false)}
    >
      {selectedApplication && (
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowDetailsModal(false)}
            >
              <Ionicons name="close" size={24} color="#333" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Application Details</Text>
            <View style={{ width: 24 }} />
          </View>

          <ScrollView style={styles.modalContent}>
            <View style={styles.detailSection}>
              <Text style={styles.detailTitle}>{selectedApplication.jobTitle}</Text>
              <Text style={styles.detailCompany}>{selectedApplication.company}</Text>
              <Text style={styles.detailLocation}>{selectedApplication.location}</Text>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Current Status</Text>
              <View style={styles.statusDetail}>
                <View
                  style={[
                    styles.statusDetailIndicator,
                    { backgroundColor: UnifiedJobApplicationService.STATUS_CONFIG?.[selectedApplication.status]?.color || '#667eea' },
                  ]}
                >
                  <Ionicons
                    name={UnifiedJobApplicationService.STATUS_CONFIG?.[selectedApplication.status]?.icon || 'document-text'}
                    size={20}
                    color="white"
                  />
                </View>
                <View style={styles.statusDetailText}>
                  <Text style={styles.statusDetailLabel}>
                    {UnifiedJobApplicationService.STATUS_CONFIG?.[selectedApplication.status]?.label || selectedApplication.status}
                  </Text>
                  <Text style={styles.statusDetailDescription}>
                    {UnifiedJobApplicationService.STATUS_CONFIG?.[selectedApplication.status]?.description || ''}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Application Info</Text>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Applied:</Text>
                <Text style={styles.detailValue}>
                  {formatDateTime(selectedApplication.applicationDate)}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Last Updated:</Text>
                <Text style={styles.detailValue}>
                  {formatDateTime(selectedApplication.lastUpdated)}
                </Text>
              </View>
              {selectedApplication.salary ? (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Salary:</Text>
                  <Text style={styles.detailValue}>{selectedApplication.salary}</Text>
                </View>
              ) : null}
              {selectedApplication.jobType ? (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Type:</Text>
                  <Text style={styles.detailValue}>{selectedApplication.jobType}</Text>
                </View>
              ) : null}
              {selectedApplication.matchScore > 0 && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Match Score:</Text>
                  <Text style={styles.detailValue}>{selectedApplication.matchScore}%</Text>
                </View>
              )}
            </View>

            {selectedApplication.statusHistory && selectedApplication.statusHistory.length > 0 && (
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Status History</Text>
                {selectedApplication.statusHistory.map((history, index) => (
                  <View key={index} style={styles.historyItem}>
                    <View style={styles.historyDot} />
                    <View style={styles.historyContent}>
                      <Text style={styles.historyStatus}>
                        {UnifiedJobApplicationService.STATUS_CONFIG?.[history.status]?.label || history.status}
                      </Text>
                      <Text style={styles.historyDate}>
                        {formatDateTime(history.date)}
                      </Text>
                      {history.note && (
                        <Text style={styles.historyNote}>{history.note}</Text>
                      )}
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Action Buttons */}
            <View style={styles.actionButtonsContainer}>
              {selectedApplication.canWithdraw &&
               selectedApplication.status !== (UnifiedJobApplicationService.APPLICATION_STATUS?.WITHDRAWN || 'withdrawn') &&
               selectedApplication.status !== (UnifiedJobApplicationService.APPLICATION_STATUS?.HIRED || 'hired') && (
                <TouchableOpacity
                  style={styles.withdrawButton}
                  onPress={() => handleWithdrawApplication(selectedApplication.id)}
                >
                  <Ionicons name="remove-circle-outline" size={20} color="#e74c3c" />
                  <Text style={styles.withdrawButtonText}>Withdraw Application</Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleDeleteApplication(selectedApplication.id)}
              >
                <Ionicons name="trash-outline" size={20} color="#e74c3c" />
                <Text style={styles.deleteButtonText}>Delete from List</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      )}
    </Modal>
  );

  const renderNotificationsModal = () => (
    <Modal
      visible={showNotifications}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowNotifications(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowNotifications(false)}
          >
            <Ionicons name="close" size={24} color="#333" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Notifications</Text>
          <View style={{ width: 24 }} />
        </View>

        <FlatList
          data={notifications}
          keyExtractor={(item) => String(item.id)}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.notificationItem, !item.read && styles.notificationUnread]}
              onPress={() => {
                if (!item.read) {
                  UnifiedJobApplicationService.markNotificationAsRead(item.id);
                  setNotifications(prev =>
                    prev.map(n => n.id === item.id ? { ...n, read: true } : n)
                  );
                }
              }}
            >
              <View style={styles.notificationContent}>
                <Text style={styles.notificationTitle}>{item.title}</Text>
                <Text style={styles.notificationMessage}>{item.message}</Text>
                <Text style={styles.notificationTime}>
                  {formatDateTime(item.timestamp)}
                </Text>
              </View>
              {!item.read && <View style={styles.unreadDot} />}
            </TouchableOpacity>
          )}
          ListEmptyComponent={
            <View style={styles.emptyNotifications}>
              <Ionicons name="notifications-outline" size={48} color="#bdc3c7" />
              <Text style={styles.emptyNotificationsText}>No notifications</Text>
            </View>
          }
        />
      </View>
    </Modal>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#667eea" />
        <Text style={styles.loadingText}>Loading applications...</Text>
      </View>
    );
  }

  const filteredApplications = getFilteredApplications();

  return (
    <View style={styles.container}>
      {renderHeader()}

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {apiError && (
          <View style={styles.apiErrorBanner}>
            <Ionicons name="cloud-offline-outline" size={16} color="#856404" />
            <Text style={styles.apiErrorText} numberOfLines={2}>
              Offline — showing cached data. Pull down to retry.
            </Text>
          </View>
        )}

        {renderAnalytics()}
        {renderFilters()}

        {filteredApplications.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="briefcase-outline" size={64} color="#bdc3c7" />
            <Text style={styles.emptyTitle}>No Applications Found</Text>
            <Text style={styles.emptySubtitle}>
              {filterStatus === 'all'
                ? 'Start applying for jobs to see them here'
                : `No applications in ${UnifiedJobApplicationService.STATUS_CONFIG?.[filterStatus]?.label || filterStatus} status`}
            </Text>
            <TouchableOpacity
              style={styles.addApplicationButton}
              onPress={() => navigation.navigate('SearchJobs')}
            >
              <Ionicons name="add" size={20} color="white" />
              <Text style={styles.addApplicationButtonText}>Find Jobs</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={filteredApplications}
            renderItem={renderApplicationCard}
            keyExtractor={(item) => String(item.id)}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
          />
        )}
      </ScrollView>

      {renderApplicationDetailsModal()}
      {renderNotificationsModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f6fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    paddingTop: 44,
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  backButton: {
    marginRight: 16,
    padding: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  notificationButton: {
    position: 'relative',
    padding: 8,
  },
  notificationBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: '#e74c3c',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationBadgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  apiErrorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3cd',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ffeeba',
  },
  apiErrorText: {
    marginLeft: 8,
    color: '#856404',
    fontSize: 12,
    flex: 1,
  },
  analyticsContainer: {
    padding: 20,
    backgroundColor: 'white',
    marginBottom: 10,
  },
  analyticsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  analyticsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  analyticCard: {
    flex: 1,
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    marginHorizontal: 4,
  },
  analyticNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#667eea',
  },
  analyticLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  successRateContainer: {
    marginTop: 16,
    alignItems: 'center',
  },
  successRateText: {
    fontSize: 14,
    color: '#27ae60',
    fontWeight: '600',
  },
  filtersContainer: {
    paddingVertical: 16,
    paddingLeft: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 12,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  filterButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: 'white',
  },
  applicationCard: {
    marginHorizontal: 20,
    marginVertical: 8,
    borderRadius: 16,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardGradient: {
    borderRadius: 16,
    padding: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  cardHeaderLeft: {
    flex: 1,
    marginRight: 16,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  company: {
    fontSize: 14,
    color: '#666',
  },
  cardHeaderRight: {
    alignItems: 'center',
  },
  statusIndicator: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e9ecef',
    borderRadius: 3,
    marginRight: 12,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
  },
  statusInfo: {
    marginBottom: 16,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  statusDescription: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
  matchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  matchBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3cd',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  matchText: {
    fontSize: 12,
    color: '#f39c12',
    marginLeft: 4,
    fontWeight: '500',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  applicationDate: {
    fontSize: 12,
    color: '#666',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  actionButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    marginRight: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: 40,
    marginBottom: 24,
  },
  addApplicationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 25,
  },
  addApplicationButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'white',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingTop: 60,
  },
  modalCloseButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  detailSection: {
    marginBottom: 24,
  },
  detailTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  detailCompany: {
    fontSize: 18,
    color: '#667eea',
    fontWeight: '600',
    marginBottom: 4,
  },
  detailLocation: {
    fontSize: 14,
    color: '#666',
  },
  detailSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  statusDetail: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDetailIndicator: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  statusDetailText: {
    flex: 1,
  },
  statusDetailLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  statusDetailDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  detailLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: 14,
    color: '#333',
    fontWeight: '400',
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  historyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#667eea',
    marginTop: 6,
    marginRight: 12,
  },
  historyContent: {
    flex: 1,
  },
  historyStatus: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  historyDate: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  historyNote: {
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  actionButtonsContainer: {
    marginTop: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  withdrawButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e74c3c',
    borderRadius: 8,
  },
  withdrawButtonText: {
    fontSize: 14,
    color: '#e74c3c',
    fontWeight: '600',
    marginLeft: 8,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#e74c3c',
    borderRadius: 8,
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#e74c3c',
    fontWeight: '600',
    marginLeft: 8,
  },
  notificationItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationUnread: {
    backgroundColor: '#f0f8ff',
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  notificationMessage: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
    marginBottom: 8,
  },
  notificationTime: {
    fontSize: 11,
    color: '#999',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#667eea',
    marginTop: 4,
    marginLeft: 8,
  },
  emptyNotifications: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyNotificationsText: {
    fontSize: 16,
    color: '#666',
    marginTop: 16,
  },
});

export default ApplicationsTrackingScreen;
